import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import { useAssetCategories, useAssetMutations } from '../api'
import { DEPRECIATION_LABELS } from '../types'
import { Select } from '@/components/ui/select'

/** Categories: the depreciation policy and the licence rule for a kind of thing. */
export function AssetCategoriesPage() {
  useBreadcrumb()
  const categories = useAssetCategories()
  const documentTypes = useDocumentTypes()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveCategory, retireCategory } = useAssetMutations()

  const [draft, setDraft] = useState<{
    id?: string; code: string; name: string; description: string
    depreciationMethod: string; usefulLifeMonths: string; residualPercent: string
    requiredDocumentTypeId: string; licenceEnforcement: string; trackSerial: boolean
  } | null>(null)
  const [retiring, setRetiring] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Asset categories"
        subtitle="How a kind of thing depreciates, and what its holder must hold."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', name: '', description: '', depreciationMethod: '',
                usefulLifeMonths: '', residualPercent: '', requiredDocumentTypeId: '',
                licenceEnforcement: '', trackSerial: false,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New category
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        No depreciation method ships. "Laptops last three years" is an accounting policy, not a
        fact about laptops, and the figure decides what somebody is charged for losing one.
        A category with no method has no book value at all, and every screen says so rather
        than showing the purchase cost.
      </p>

      <Card>
        {categories.isPending && <Skeleton className="m-4 h-24" />}
        {categories.isError && (
          <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
        )}
        {categories.isSuccess && categories.data.length === 0 && (
          <EmptyState
            title="No category is defined"
            description="Nothing can be added to the register until one exists. A category carries the depreciation policy, whether serial numbers are tracked, and any licence the holder needs."
          />
        )}
        {categories.isSuccess && categories.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Category</th>
                  <th className="p-3 text-start font-medium">Depreciation</th>
                  <th className="p-3 text-start font-medium">Licence needed</th>
                  <th className="p-3 text-end font-medium">Assets</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {categories.data.map((category) => (
                  <tr key={category.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{category.code}</td>
                    <td className="p-3">{category.name}</td>
                    <td className="p-3 max-w-md text-xs text-slate-600">
                      {category.depreciationNote}
                    </td>
                    <td className="p-3 text-xs text-slate-600">
                      {category.requiredDocumentTypeName
                        ? `${category.requiredDocumentTypeName} — ${
                            category.licenceEnforcement === 'BLOCK'
                              ? 'handover is blocked without it'
                              : 'handover is allowed with a warning'}`
                        : 'None'}
                    </td>
                    <td className="p-3 text-end">{category.assetCount}</td>
                    <td className="p-3 text-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setError(null)
                          setDraft({
                            id: category.id,
                            code: category.code,
                            name: category.name,
                            description: category.description ?? '',
                            depreciationMethod: category.depreciationMethod ?? '',
                            usefulLifeMonths: category.usefulLifeMonths?.toString() ?? '',
                            residualPercent: category.residualPercent?.toString() ?? '',
                            requiredDocumentTypeId: category.requiredDocumentTypeId ?? '',
                            licenceEnforcement: category.licenceEnforcement ?? '',
                            trackSerial: category.trackSerial,
                          })
                        }}
                      >
                        Edit
                      </Button>
                      {category.active && (
                        <Button
                          type="button" variant="outline"
                          onClick={() => {
                            setError(null)
                            setRetiring({ id: category.id, name: category.name })
                          }}
                        >
                          Retire
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this category'}?`}
        description="It stops being offered for new assets. Refused while assets are still filed under it, because a register whose categories no longer exist cannot be reported on."
        confirmLabel="Retire it"
        loading={retireCategory.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retireCategory.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That category could not be retired.'),
          })
        }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        size="lg"
        title={draft?.id ? 'Edit category' : 'New category'}
        description="Leave the depreciation method blank for things nobody depreciates."
        submitLabel="Save category"
        loading={saveCategory.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveCategory.mutateAsync({
                id: draft.id,
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  code: draft.code,
                  name: draft.name,
                  description: draft.description || null,
                  depreciationMethod: draft.depreciationMethod || null,
                  usefulLifeMonths: draft.usefulLifeMonths
                    ? Number(draft.usefulLifeMonths) : null,
                  residualPercent: draft.residualPercent ? Number(draft.residualPercent) : null,
                  requiredDocumentTypeId: draft.requiredDocumentTypeId || null,
                  licenceEnforcement: draft.licenceEnforcement || null,
                  trackSerial: draft.trackSerial,
                  active: true,
                },
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That category could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="category-code">Code</Label>
              <Input
                id="category-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="category-name">Name</Label>
              <Input
                id="category-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="category-method">Depreciation method</Label>
              <Select
                id="category-method"
                className="w-full"
                value={draft.depreciationMethod}
                onChange={(event) =>
                  setDraft({ ...draft, depreciationMethod: event.target.value })}
              >
                <option value="">None — these have no book value</option>
                {Object.entries(DEPRECIATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                No default: the method and the life are an accounting policy this system has no
                basis for choosing.
              </p>
            </div>
            <div>
              <Label htmlFor="category-life">Useful life, months</Label>
              <Input
                id="category-life" type="number" value={draft.usefulLifeMonths}
                onChange={(event) => setDraft({ ...draft, usefulLifeMonths: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Required once a method is chosen, and ignored without one.
              </p>
            </div>
            <div>
              <Label htmlFor="category-residual">Residual, %</Label>
              <Input
                id="category-residual" type="number" value={draft.residualPercent}
                onChange={(event) => setDraft({ ...draft, residualPercent: event.target.value })}
              />
            </div>
            <div className="flex items-end gap-2">
              <input
                id="category-serial" type="checkbox" className="size-4"
                checked={draft.trackSerial}
                onChange={(event) => setDraft({ ...draft, trackSerial: event.target.checked })}
              />
              <Label htmlFor="category-serial">Every one has a serial number</Label>
            </div>
            <div>
              <Label htmlFor="category-document">Licence the holder must have</Label>
              <Select
                id="category-document"
                className="w-full"
                value={draft.requiredDocumentTypeId}
                onChange={(event) =>
                  setDraft({ ...draft, requiredDocumentTypeId: event.target.value })}
              >
                <option value="">None is required</option>
                {documentTypes.data?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Checked against the person's own documents, so it expires in the same report as
                their passport.
              </p>
            </div>
            <div>
              <Label htmlFor="category-enforcement">When they do not have it</Label>
              <Select
                id="category-enforcement"
                className="w-full"
                value={draft.licenceEnforcement}
                onChange={(event) =>
                  setDraft({ ...draft, licenceEnforcement: event.target.value })}
              >
                <option value="">Choose one…</option>
                <option value="BLOCK">Refuse the handover</option>
                <option value="WARN">Allow it, and record the warning</option>
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                Required once a document is named: a requirement with no answer permits
                everything.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
